import React, { Component } from 'react';
import { Badge } from 'react-bootstrap';
import Select from 'components/controls/select';
import StatusIndicator from 'components/controls/statusIndicator';
import UserIdentity from 'components/controls/userIdentity';
import Utils from 'context/utils';
import './revisionSelect.css';

// The revision selector. IT TAKES NO API: the page owns the fetch and this calls `onRevisionsNeeded`
// when the menu opens, because a presentational header is what two existing suites protect. Height and
// palette belong to select.js. docs/specs/2026-09-18-revision-selection.md#the-selector

// The options, and the option the control shows as its value. `label` stays a PLAIN STRING because
// react-select's filter stringifies it and JSX there breaks typed search; the current option is built
// from the page's own `revision`, so the trigger never sits empty waiting on the list.
export function buildRevisionOptions(revisions, revision) {
  const revisionOptions = [];
  let currentRevisionOption = null;
  for ( const candidate of revisions || [] ) {
    // The list is never refreshed; the page's copy of the revision on screen is the one it fetched.
    const shown = !!revision && candidate.id === revision.id;
    const option = shown
      ? { value: revision, label: optionLabel(revision) }
      : { value: candidate, label: optionLabel(candidate) };
    if ( shown ) {
      currentRevisionOption = option;
    }
    revisionOptions.push(option);
  }
  // The list is one page -- the revision on screen may sit off it entirely. Unshifting into the
  // array itself, not just returning it alongside, is what buildSessionOptions has always done.
  if ( !currentRevisionOption && revision ) {
    currentRevisionOption = { value: revision, label: optionLabel(revision) };
    revisionOptions.unshift(currentRevisionOption);
  }
  return { revisionOptions, currentRevisionOption };
}

function optionLabel(revision) {
  return revision.comment || revision.id;
}

// Presentation only, so none of it reaches the filter. Layout depends ONLY on `context` -- "value"
// for the closed trigger, "menu" for a row -- never on which option is selected, which react-select's
// own tint says. Pills, marks and what each surface carries: the spec's "The selector".
export function formatRevisionOptionLabel(option, meta, context) {
  const revision = option.value;
  const { defaultRevisionId, users } = meta || {};
  const isDefault = !!revision.id && revision.id === defaultRevisionId;

  if ( "value" === context ) {
    return (
      <span className="revision-select-value">
        <span className="revision-select-value-comment">{optionLabel(revision)}</span>
        <Badge variant="secondary" className="title-tag-badge">{revision.phase}</Badge>
        {!isDefault &&
          <Badge variant="secondary" className="title-tag-badge title-tag-badge-warning">not default</Badge>
        }
      </span>
    );
  }

  // The header has no api, so an author it cannot resolve is left out rather than printed as the raw
  // uuid the API addresses the account by — a name's slot is no place for a machine value.
  const author = ( users && revision.user_id ) ? users[revision.user_id] : null;
  return (
    <div className="revision-select-option">
      <span className="record-title">
        <span className="record-title-main">
          <span className="revision-select-option-comment">{optionLabel(revision)}</span>
          <Badge variant="secondary" className="title-tag-badge">{revision.phase}</Badge>
          {isDefault &&
            <Badge variant="secondary" className="title-tag-badge">default</Badge>
          }
        </span>
        <StatusIndicator status={revision.status}/>
      </span>
      <div className="record-meta">
        <span className="record-meta-value revision-select-option-id">
          <code>{revision.id}</code>
        </span>
      </div>
      <span className="revision-select-option-meta text-muted">
        {author &&
          <UserIdentity user={author} link={false} showIdentifier={false} strong={false}/>
        }
        {/* The same separator the Revisions tab writes, spelled the same way: a flex `gap` either side
            of a dot span reads wider than the nbsp that joins them there. */}
        {( author && revision.created_at ) && <>&nbsp;&middot;&nbsp;</>}
        {revision.created_at &&
          <span>{Utils.formatTimeAgo(revision.created_at)}</span>
        }
      </span>
    </div>
  );
}

export default class RevisionSelect extends Component {
  render() {
    const revision = this.props.revision;
    // Nothing to render from. The page shows "Loading revision..." until it has resolved one, and the
    // two header suites mount with no revision at all.
    if ( !revision ) {
      return null;
    }

    const meta = { defaultRevisionId: this.props.defaultRevisionId, users: this.props.users };
    const { revisionOptions, currentRevisionOption } = buildRevisionOptions(this.props.revisions, revision);
    return (
      <div className="revision-select mr-2">
        <Select
          classNamePrefix="revision-select"
          aria-label="Revision"
          options={revisionOptions}
          value={currentRevisionOption}
          formatOptionLabel={(option, { context }) => formatRevisionOptionLabel(option, meta, context)}
          // Every open asks. Loading the list once is the page's business, and a page that failed to
          // load it gets asked again on the next open rather than showing an empty menu for ever.
          onMenuOpen={() => this.props.onRevisionsNeeded && this.props.onRevisionsNeeded()}
          onChange={(option) => this.props.onSelect && this.props.onSelect(option.value)}
          menuPortalTarget={document.body}
        />
      </div>
    );
  }
}
